import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
// Asset uses bt-common's canonical MessageAsset (the persisted shape on
// userMessages.asset). Local `Asset` from common/types/interface.ts is a
// dupe — tracked in #41.
import { LinkPreview, MessageAsset as Asset } from '@bt-common/schemas/bt/user-message';
import { scheduledMessageEditorActions } from '@common/reducers/ScheduledMessageEditorReducer';
import { RetryLimitUnit } from '@common/types/interface';
import { debounce } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { Poll } from './schedule-message-types';
import ScheduledMessageEditor from './scheduled-message-editor';
import moment from 'moment';

export type ScheduledMessageEditorState = {
    message: string;
    isPoll: boolean;
    poll: Poll;
    asset?: Asset;
    imageId: string;
    sendLinkPreview: boolean;
    linkPreview: LinkPreview;
    templateId: string;
    dueDate: string;
    /** BE#657 — true once the user explicitly picked a date/time; synced to the draft
     *  so the Send gate only enables once a real choice exists (not a seeded ~now). */
    dueDateSetByUser: boolean;
    rruleset: string;
    isRecurring: boolean;
    cancelIfReceived: boolean;
    retryLimitDuration?: number;
    retryLimitUnit?: RetryLimitUnit;
};

type ScheduledMessageEditorWrapperProps = {
    // Incoming state from parent context
    incomingMessage: string;
    incomingIsPoll: boolean;
    incomingPoll: Poll;
    incomingAsset: Asset;
    incomingImageId: string;
    incomingSendLinkPreview: boolean;
    incomingLinkPreview: LinkPreview;
    incomingTemplateId: string;
    incomingDueDate: string;
    incomingRruleset: string;
    incomingIsRecurring: boolean;
    incomingCancelIfReceived: boolean;
    incomingRetryLimitDuration?: number;
    incomingRetryLimitUnit?: RetryLimitUnit;
    
    // Callback for syncing back to parent context
    onStateChange: (state: ScheduledMessageEditorState) => void;
    
    // Other props
    isPreview?: boolean;
    editorContext: 'scheduleMessage' | 'scheduleCampaign';
}

export default function ScheduledMessageEditorWrapper(props: ScheduledMessageEditorWrapperProps) {
    const dispatch = useAppDispatch();
    
    // Get current state from ScheduledMessageEditorReducer
    const editorState = useAppSelector((state: any) => state.ScheduledMessageEditorReducer);
    const user = useAppSelector((state: any) => state.UserReducer.user);
    const defaultSchedulingTime = user?.preferences?.defaultSchedulingTime;




    // Initialize ScheduledMessageEditorReducer from incoming props - single useEffect to prevent multiple re-renders
    // STRICT MODE: Run only on mount to prevent infinite loops where internal state changes -> sync to parent -> parent updates props -> wrapper re-initializes
    useEffect(() => {
        let initialDueDate = props.incomingDueDate || '';

        // Apply default scheduling time if user has a specific time preference
        if (user?.preferences?.defaultSchedulingTime) {
            const defaultTime = user.preferences.defaultSchedulingTime;
            // Check if format is HH:mm
            if (/^\d{1,2}:\d{1,2}$/.test(defaultTime)) {
                const [hours, minutes] = defaultTime.split(':').map(Number);
                if (!isNaN(hours) && !isNaN(minutes)) {
                    // If we have an incoming due date (e.g., from calendar), use that date but set the default time
                    // Otherwise, use today's date with the specified time
                    const baseDate = props.incomingDueDate 
                        ? moment(props.incomingDueDate).local() 
                        : moment().local();
                    
                    const dateWithTime = baseDate.set({
                        hour: hours,
                        minute: minutes,
                        second: 0,
                        millisecond: 0
                    });
                    
                    initialDueDate = dateWithTime.utc().toISOString();
                }
            }
        } else {
            initialDueDate = props.incomingDueDate
        }

        dispatch(scheduledMessageEditorActions.loadMessageData({
            message: props.incomingMessage || '',
            isPoll: props.incomingIsPoll || false,
            poll: props.incomingPoll || {},
            asset: props.incomingAsset,
            imageId: props.incomingImageId || '',
            sendLinkPreview: props.incomingSendLinkPreview || false,
            linkPreview: props.incomingLinkPreview || {},
            templateId: props.incomingTemplateId || '',
            dueDate: initialDueDate,
            rruleset: props.incomingRruleset || '',
            isRecurring: props.incomingIsRecurring || false,
            cancelIfReceived: props.incomingCancelIfReceived || false,
            retryLimitDuration: props.incomingRetryLimitDuration,
            retryLimitUnit: props.incomingRetryLimitUnit,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Debounced callback to sync back to parent context
    const debouncedSyncToParent = useMemo(
        () => debounce((state: ScheduledMessageEditorState) => {
            props.onStateChange(state);
        }, 200),
        [props.onStateChange]
    );

    // Listen to internal state changes and sync back to parent context
    useEffect(() => {
        const stateToSync: ScheduledMessageEditorState = {
            message: editorState?.message,
            isPoll: editorState?.isPoll,
            poll: editorState?.poll,
            asset: editorState?.asset,
            imageId: editorState?.imageId,
            sendLinkPreview: editorState?.sendLinkPreview,
            linkPreview: editorState?.linkPreview,
            templateId: editorState?.templateId,
            dueDate: editorState?.dueDate,
            dueDateSetByUser: editorState?.dueDateSetByUser, // BE#657 — carry the explicit-pick flag to the draft
            rruleset: editorState?.rruleset,
            isRecurring: editorState?.isRecurring,
            cancelIfReceived: editorState?.cancelIfReceived,
            retryLimitDuration: editorState?.retryLimitDuration,
            retryLimitUnit: editorState?.retryLimitUnit,
        };

        debouncedSyncToParent(stateToSync);
    }, [
        editorState?.message,
        editorState?.isPoll,
        editorState?.poll,
        editorState?.asset,
        editorState?.imageId,
        editorState?.sendLinkPreview,
        editorState?.linkPreview,
        editorState?.templateId,
        editorState?.dueDate,
        editorState?.dueDateSetByUser,
        editorState?.rruleset,
        editorState?.isRecurring,
        editorState?.cancelIfReceived,
        editorState?.retryLimitDuration,
        editorState?.retryLimitUnit,
        debouncedSyncToParent,
    ]);

    return (
        <ScheduledMessageEditor
            isPreview={props.isPreview}
            editorContext={props.editorContext}
        />
    );
}